import type { DocMeta } from '@blocksuite/store';
import { Input, Menu, MenuItem, Scrollable } from '@pique/component';
import { useService, WorkspaceService } from '@toeverything/infra';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './multi-select.css';

export const TitleMultiSelect = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const workspace = useService(WorkspaceService).workspace;

  const uniqueTitles = useMemo(() => {
    const metas = workspace.docCollection.meta.docMetas;
    const titleSet = new Set<string>();
    metas
      .filter((meta: DocMeta) => !meta.trash && !(meta.tags || []).includes('temp:peek'))
      .forEach((meta: DocMeta) => {
        titleSet.add(meta.title || 'Untitled');
      });
    return Array.from(titleSet).sort((a, b) => a.localeCompare(b));
  }, [workspace.docCollection.meta.docMetas]);

  const filteredTitles = useMemo(() => {
    if (!search.trim()) return uniqueTitles;
    const s = search.toLowerCase();
    return uniqueTitles.filter(title => title.toLowerCase().includes(s));
  }, [uniqueTitles, search]);

  const handleToggle = useCallback(
    (title: string, e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const isSelected = value.includes(title);
      if (isSelected) {
        onChange(value.filter(v => v !== title));
      } else {
        onChange([...value, title]);
      }
    },
    [onChange, value]
  );

  const displayText = value.length ? value.join(', ') : 'Select meetings';

  const items = (
    <div style={{ width: 280 }}>
      <div style={{ padding: '8px 12px' }}>
        <Input
          placeholder="Search meetings..."
          value={search}
          onChange={setSearch}
          style={{ width: '100%' }}
        />
      </div>
      <Scrollable.Root>
        <Scrollable.Viewport className={styles.optionList}>
          {filteredTitles.length === 0 ? (
            <MenuItem disabled>No meetings found</MenuItem>
          ) : (
            filteredTitles.map(title => {
              const isSelected = value.includes(title);
              return (
                <MenuItem
                  key={title}
                  checked={isSelected}
                  onClick={(e: MouseEvent) => handleToggle(title, e)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>{title}</span>
                </MenuItem>
              );
            })
          )}
        </Scrollable.Viewport>
        <Scrollable.Scrollbar className={styles.scrollbar} />
      </Scrollable.Root>
    </div>
  );

  return (
    <Menu items={items}>
      <div className={styles.content} style={{ cursor: 'pointer' }}>
        {value.length > 0 ? (
          <span className={styles.text}>{displayText}</span>
        ) : (
          <span className={styles.text} style={{ opacity: 0.5 }}>
            {displayText}
          </span>
        )}
      </div>
    </Menu>
  );
};
